import { ISSUE, ISSUE_TARGET, configIssue } from '../catalog/issue';
import { RCU_LIMITS } from '../catalog/limits';
import { CHANNEL_KIND } from '../catalog/channel';
import type { RcuConfig, RcuIdentity } from '../model';
import { wiredLoads } from '../query/grouping';
import type { CommandResult } from '../model';
import { fail } from '../result';
import { changedBy } from './outcome';

/** Identity fields a technician can edit. Output count has its own command. */
export type RcuIdentityPatch = Partial<Omit<RcuIdentity, 'outputCount'>>;

export const updateIdentity = (
  config: RcuConfig,
  patch: RcuIdentityPatch,
  now: string,
): CommandResult<RcuIdentity> => {
  const rcu = { ...config.rcu, ...patch };
  return changedBy({ ...config, rcu }, rcu, now);
};

const loadsAboveOutput = (config: RcuConfig, outputCount: number) =>
  wiredLoads(config).filter(
    (load) => load.channel.kind === CHANNEL_KIND.RCU && load.channel.channel > outputCount,
  );

/**
 * Sets the onboard output count. Shrinking below a wired output is refused
 * rather than silently unwiring, so the technician decides what happens to
 * those loads.
 */
export const setOutputCount = (
  config: RcuConfig,
  outputCount: number,
  now: string,
): CommandResult<number> => {
  if (outputCount < RCU_LIMITS.MIN_OUTPUTS || outputCount > RCU_LIMITS.MAX_OUTPUTS) {
    return fail(
      configIssue(ISSUE.OUTPUT_COUNT_OUT_OF_RANGE, { kind: ISSUE_TARGET.RCU }, String(outputCount)),
    );
  }

  const blocked = loadsAboveOutput(config, outputCount);
  if (blocked.length > 0) {
    const names = blocked.map((load) => load.name).join(', ');
    const count = String(blocked.length);
    return fail(
      configIssue(
        ISSUE.OUTPUT_COUNT_BLOCKED,
        { kind: ISSUE_TARGET.RCU },
        `${count} load(s) wired above output ${outputCount}: ${names}`,
      ),
    );
  }

  return changedBy({ ...config, rcu: { ...config.rcu, outputCount } }, outputCount, now);
};
